// src/pages/Coach/index.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  mode=agreement, from the agreement card on Today: rehearse what
//               the manager showed them. Only the flag travels; the server
//               reads the correction.
//   2026-08-29  The transcript comes from the server. localStorage paints it
//               instantly on load and the server copy replaces it, so the
//               conversation follows somebody between a phone and a desk and
//               survives clearing site data.
//   2026-08-28  Five things that made this hard to actually talk to.
//               1. A failed send lost the message. The toast said try again and
//                  the thing you typed was gone, so "try again" meant retype it.
//                  The failed turn now stays in the thread with a Try again on
//                  it, which is also the only honest place to put a failure: in
//                  the conversation, where it happened.
//               2. The box never grew. rows={1} with no auto-size, so writing
//                  three sentences about a customer meant composing through a
//                  one line slot. It grows to 140px now, then scrolls.
//               3. Enter always sent. On a phone there is no shift, so a
//                  salesperson physically could not write a second line. Enter
//                  only sends where there is a real keyboard, which
//                  (pointer: fine) is a good proxy for.
//               4. Clearing was one unconfirmed tap, and since transcripts live
//                  only in this browser, that tap was the whole history. Asks
//                  once now, and forgets it asked after four seconds.
//               5. Replies were invisible to a screen reader. The thread is a
//                  polite live region and the composer is labelled.
//   2026-08-02  Header standardized; Phase 1 checkpoint committed (e765830).
//   2026-08-01  New: full-height chat, live training context, local transcript
// NEXT: Persist transcripts to Supabase in Phase 2; coach memory feeds prompt
// The J13 Coach chat. Full-height conversation surface, mobile-first. Talks to
// the coach Netlify function (real Anthropic API) with the user's live
// training context so the coach keeps them on task. Transcript persists
// locally per user.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams, Link as RouterLink } from 'react-router-dom'
import { STEPS } from '../../content/steps'
import { parseCoachLinks } from '../../lib/coachLinks'
import { supabase } from '../../lib/supabase'
import LastTaught from './LastTaught.jsx'
import { followupOpener } from '../../lib/morningRoutine.js'
import NotHowISayIt from './NotHowISayIt'
import {
  Box, Flex, VStack, HStack, Text, Textarea, IconButton, Spinner,
  usePrefersReducedMotion,
  Wrap, WrapItem, Button, useMediaQuery,
} from '@chakra-ui/react'
import { ArrowUp, RotateCcw, Sparkles, AlertCircle, Mic, ArrowUpRight } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useDictation } from '../../hooks/useDictation'
import { firstNameFor } from '../../lib/names.js'
import { getProgress } from '../../lib/progressStore'
import { sendToCoach, loadTranscript, saveTranscript, clearTranscript, loadRemoteTranscript } from '../../lib/coachApi'

const SUGGESTIONS = [
  'Quiz me on my current step',
  'Run me a drill on my current step',
  'Practice handling "I need to think about it"',
  'What should I work on today?',
]

// THE AUTHOR OPENS ON A DIFFERENT PAGE, and this is why.
//
// Tyler, 2026-09-26, after watching Jazz use it: the coach opened by telling him
// what it still needed from him, which turns his own product into a homework
// list the moment he signs in. He also said it felt slow.
//
// Both were the same cause. The author path made a full model round trip BEFORE
// he had typed anything, to compose a "welcome back" that then read out the
// backlog. So opening the page cost him a wait and then handed him a chore.
//
// This is written rather than generated. It is instant, it costs nothing, and
// it says exactly what it should say every time. A greeting is the one message
// in this product that does not need a model, because it is the same sentence
// to the same person about nothing that has happened yet.
//
// IT NAMES THE DIRECTION, and that is the second half of the same fix. Tyler,
// 2026-09-26, on what he wants Jazz to hear: "Oh, hey Jazz, yep, we're steering
// in a direction, and we're going towards this." A greeting that says nothing
// about where this is going leaves him to ask, and the thing he should never
// have to do is interview his own product about its plan.
//
// NO STEP NUMBERS AND NO COUNTS IN THIS COPY. Those move every week and this
// string does not. The live version of that answer is computed per request in
// coverage.js and reaches the model, which is the right place for a fact with
// a shelf life.
const AUTHOR_SUGGESTIONS = [
  'What are we working on?',
  'Read me back something I said',
  'Play a customer and let me sell you',
  'Here is how I actually open a deal',
]

// A send that failed, shown where it happened rather than as a toast that
// disappears and takes the context with it.
function FailedTurn({ onRetry, busy }) {
  return (
    <HStack align="flex-start" spacing={3} w="full">
      <Flex boxSize="30px" borderRadius="full" bg="surface" color="danger" align="center" justify="center" flexShrink={0} mt={1}>
        <AlertCircle size={15} />
      </Flex>
      <Box maxW="82%">
        <Text fontSize="body-sm" color="inkMuted" lineHeight={1.6}>
          That did not go through. Your message is still here.
        </Text>
        <Button size="sm" minH="40px" px={4} variant="outline" borderRadius="pill" mt={2} onClick={onRetry} isLoading={busy} loadingText="Sending">
          Try again
        </Button>
      </Box>
    </HStack>
  )
}


// The coach can end a turn with a single OPTIONS line to offer choices. The
// browser strips it and draws buttons, so a conversation that looks like typing
// is mostly tapping.
//
// Somebody on a dealership floor between customers is not going to write
// paragraphs into a chat box, and asking them to is how a good idea becomes a
// form nobody finishes.
//
// Parsed rather than tool-called on purpose: a tool round trip doubles the
// latency of every question, and this is a line of text the model is already
// good at producing.
function splitOptions(content) {
  const m = /\n?OPTIONS:\s*(.+)\s*$/i.exec(content || '')
  if (!m) return { text: content, options: [] }
  const options = m[1]
    .split('|')
    .map((x) => x.trim())
    .filter(Boolean)
    .slice(0, 4)
  return { text: content.slice(0, m.index).trim(), options }
}

// The coach's words, with anything it pointed at turned into something you can
// tap. parseCoachLinks drops a token it cannot resolve, so a hallucinated step
// 14 leaves prose rather than a dead button.
function CoachText({ content }) {
  const parts = parseCoachLinks(content, STEPS)
  return (
    <>
      {parts.map((part, i) =>
        part.type === 'text' ? (
          <Text key={i} fontSize="body" lineHeight={1.6} whiteSpace="pre-wrap">
            {part.value}
          </Text>
        ) : (
          <Button
            key={i}
            as={RouterLink}
            to={part.to}
            size="sm"
            variant="outline"
            rightIcon={<ArrowUpRight size={14} />}
            my={2}
            whiteSpace="normal"
            textAlign="left"
            h="auto"
            py={2}
          >
            {part.label}
          </Button>
        )
      )}
    </>
  )
}

function Bubble({ message }) {
  const isUser = message.role === 'user'
  return (
    <HStack align="flex-start" justify={isUser ? 'flex-end' : 'flex-start'} w="full" spacing={3}>
      {!isUser && (
        <Flex
          boxSize="30px"
          borderRadius="full"
          bg="ink"
          color="onInk"
          align="center"
          justify="center"
          flexShrink={0}
          mt={1}
        >
          <Sparkles size={14} />
        </Flex>
      )}
      {/* ONLY THE PERSON GETS A BUBBLE.
          The coach's own words are just text on the window now, which is the
          same instruction as the design pass: do not put content in containers.
          A card around every reply drew a box around the one thing on the page
          worth reading, and two facing bubbles make a transcript rather than a
          conversation. The avatar and the alignment already say who is talking.

          The measure is narrower than the bubble was. A 750px line of prose is
          a line you lose your place in, and the coach writes prose. */}
      <Box
        maxW={isUser ? { base: '85%', md: '76%' } : { base: '100%', md: '68ch' }}
        bg={isUser ? 'ink' : 'transparent'}
        color={isUser ? 'onInk' : 'ink'}
        px={isUser ? 4 : 0}
        py={isUser ? 2.5 : 0}
        pt={isUser ? 2.5 : 0.5}
        borderRadius={isUser ? '18px' : 0}
        borderTopRightRadius={isUser ? '6px' : 0}
      >
        {isUser ? (
          <Text fontSize="body" lineHeight={1.6} whiteSpace="pre-wrap">
            {message.content}
          </Text>
        ) : (
          <CoachText content={splitOptions(message.content).text} />
        )}
      </Box>
    </HStack>
  )
}

// THE REPLY AS IT IS BEING WRITTEN.
//
// Deliberately NOT the Bubble component, and the reason matters. A bubble
// carries the affordances of a finished turn: the correction control, the
// option buttons, the whole apparatus of something you can act on. Half a
// sentence is not something to act on yet, and offering a button under a
// sentence that has not finished is the interface equivalent of interrupting.
//
// It matches Bubble's geometry exactly, so when the real turn replaces it
// nothing moves. A layout shift at the end of every reply would undo most of
// what streaming buys.
function LiveTurn({ text }) {
  return (
    <HStack align="flex-start" justify="flex-start" w="full" spacing={3}>
      <Flex
        boxSize="30px"
        borderRadius="full"
        bg="ink"
        color="onInk"
        align="center"
        justify="center"
        flexShrink={0}
        mt={1}
      >
        <Sparkles size={14} />
      </Flex>
      <Box maxW="82%" minW={0}>
        {/* Plain text while it streams rather than CoachText. Link parsing on
            a half written sentence turns a step reference into a link and then
            back into text as the next fragment lands, which flickers. The
            finished turn does the parsing once, when the words have stopped
            moving. */}
        <Text fontSize="body" lineHeight={1.6} whiteSpace="pre-wrap">
          {text}
          <Box
            as="span"
            display="inline-block"
            w="2px"
            h="1em"
            ml="2px"
            bg="accent.500"
            verticalAlign="text-bottom"
            sx={{
              animation: 'coachCaret 1s steps(2, start) infinite',
              '@keyframes coachCaret': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0 } },
              // Somebody who has asked for less motion gets a caret that is
              // present and still, rather than no caret at all: it is telling
              // them the reply is unfinished, which is information.
              '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0.6 },
            }}
          />
        </Text>
      </Box>
    </HStack>
  )
}

// The bubble plus, for Level 00 only, the one tap that turns a wrong answer
// into a word track.
function CoachTurn({ message, previous, canCorrect, stepNumber, isLast, onChoose }) {
  const { options } = message.role === 'assistant' ? splitOptions(message.content) : { options: [] }
  return (
    <Box w="full">
      <Bubble message={message} />

      {/* Only under the newest turn. Live buttons halfway up a transcript
          invite somebody to answer a question that has moved on. */}
      {isLast && options.length > 0 && (
        <Wrap spacing={2} pl="42px" pt={2.5}>
          {options.map((o) => (
            <WrapItem key={o}>
              <Button
                size="sm"
                variant="outline"
                borderColor="lineStrong"
                color="ink"
                bg="transparent"
                minH="44px"
                _hover={{ bg: 'surface', borderColor: 'ink' }}
                onClick={() => onChoose(o)}
              >
                {o}
              </Button>
            </WrapItem>
          ))}
        </Wrap>
      )}
      {canCorrect && message.role === 'assistant' && (
        <NotHowISayIt
          exchange={{ user: previous?.role === 'user' ? previous.content : null, assistant: message.content }}
          stepNumber={stepNumber}
        />
      )}
    </Box>
  )
}

export default function Coach() {
  const { user, profile } = useAuth()
  // Jazz pressing "that is not how I say it" means the method is wrong, which
  // we can act on. A salesperson pressing it would mean "that confused me",
  // which is a different and separately useful thing that does not exist yet.
  const canCorrect = profile?.role === 'admin' && !profile?.dealership_id
  const [failed, setFailed] = useState(false)
  const [confirmReset, setConfirmReset] = useState(false)
  // A precise pointer is a good proxy for a real keyboard, which is what
  // decides whether Enter should send or make a new line.
  const [hasKeyboard] = useMediaQuery('(pointer: fine)', { ssr: false })
  const inputRef = useRef(null)
  const userId = user?.id
  // Null when we genuinely do not know the name. Never substitute a greeting
  // word here: callers below treat this as a name, and "Ready when you are,
  // there." reads as a typo. The greeting fallback belongs at the render site.
  const firstName = firstNameFor(profile, user)

  const [messages, setMessages] = useState(() => loadTranscript(userId))
  const [input, setInput] = useState('')
  const [copiedPath, setCopiedPath] = useState(false)
  const reduceMotion = usePrefersReducedMotion()

  // Dictated text lands in the box, it does not send itself. Jazz's word tracks
  // become what the coach teaches everybody, so he gets to see them before they
  // count. Appends rather than replaces, so a second take adds to the first.
  const dictation = useDictation({
    onText: (text) => {
      setInput((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text))
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.focus()
          autoGrow(inputRef.current)
        }
      })
    },
  })
  const [sending, setSending] = useState(false)
  // The reply as it arrives, a fragment at a time. Empty whenever nothing is
  // in flight, which is what the render below keys off.
  const [streaming, setStreaming] = useState('')
  const bottomRef = useRef(null)

  const progress = useMemo(() => getProgress(userId), [userId])

  const [searchParams, setSearchParams] = useSearchParams()
  // A drill is requested by link, from the step you just cleared, so practice
  // starts from the moment you finished the thing rather than from a menu.
  const practiceStep = Number(searchParams.get('practice')) || null
  // A question carried in from the dock, so the coach opens already asked
  // rather than showing an empty box to somebody who had a question a second
  // ago and has now forgotten how they wanted to phrase it.
  const askParam = searchParams.get('ask')
  // Jazz teaching the coach his real scripts, rather than the coach teaching
  // anybody anything. Level 00 only, re-checked on the server.
  const methodMode = searchParams.get('mode') === 'method'
  const introMode = searchParams.get('mode') === 'intro'
  // Arriving from the morning routine to practise a callback. The count comes
  // with it so the opener is in the rep's own words. HELD FOR THE WHOLE VISIT,
  // unlike the other modes: the parameter is dropped after the opening turn,
  // but the follow up block has to stay on every turn, because the one rule it
  // carries (describe the situation, never the person) matters most on the
  // turn where somebody starts typing a name.
  const followupParam = searchParams.get('mode') === 'followup'
  const [followup] = useState(() =>
    followupParam ? { unsold: Math.min(5, Math.max(1, Number(searchParams.get('unsold')) || 1)) } : null
  )
  // Arriving from the agreement card on Today to rehearse what their manager
  // showed them. Held for the whole visit like the follow up, because the
  // server re-reads the correction on every turn and the rehearsal is several
  // turns long. The client sends only the flag; coach.js reads the row itself.
  const agreementParam = searchParams.get('mode') === 'agreement'
  const [agreement] = useState(() => agreementParam)
  const [drilledBefore, setDrilledBefore] = useState(false)

  const context = useMemo(
    () => ({
      // A second run at the same step asks for a harder situation. Somebody
      // who has already done this once does not need the easy customer again.
      practice: practiceStep ? { stepNumber: practiceStep, harder: drilledBefore } : undefined,
      followup: followup || undefined,
      agreement: agreement || undefined,
      firstName,
      role: profile?.role || null,
      currentStep: progress.currentStep
        ? {
            number: progress.currentStep.number,
            title: progress.currentStep.title,
            status: progress.currentStep.status,
          }
        : null,
      streakDays: progress.streakDays,
      openCommitments: progress.openCommitments.map((c) => c.body),
    }),
    [firstName, profile?.role, progress, practiceStep, followup, agreement]
  )

  // THE COACH SAYS THE FIRST THING WHEN HE COMES BACK.
  //
  // Only for the two people who write the method, and only after a real gap.
  // What Jazz returned to on 2026-09-10 was the last line on screen from the
  // day a limit cut him off mid sentence, with nothing to say that three hours
  // of teaching had landed anywhere.
  //
  // It cannot repeat itself. The greeting is written to the transcript as it is
  // spoken, so on the next load it IS the last message and the gap is zero.
  // The ref only guards a double mount inside one visit.
  // THE UNPROMPTED GREETING IS GONE, 2026-09-26.
  //
  // It made a full model round trip on OPEN, before he had typed a word, to
  // compose a welcome back that then read the backlog out to him. Two costs for
  // one feature: the page sat there while an Opus call ran, and what it finally
  // said was a list of what he still owed us.
  //
  // Tyler watched him use it and named both. "It takes a while to load", and
  // the coach opening with what it needs turns his own product into homework.
  //
  // The replacement is the empty state above: written, instant, free, and the
  // same warm sentence every time. A greeting is the one message here that
  // never needed a model, because it is the same words to the same person about
  // nothing that has happened yet.
  //
  // What was genuinely good in the old one and is worth rebuilding LATER, on a
  // path that does not block the open: quoting one thing he taught us back in
  // his own words. That earned its place. It just should not cost him a wait.

  useEffect(() => {
    // Paint the cached copy immediately. A coach that shows a blank screen for
    // a third of a second on every open reads as broken rather than loading.
    setMessages(loadTranscript(userId))

    let alive = true
    if (!userId) return undefined
    loadRemoteTranscript(userId).then(({ messages: remote, sessionId }) => {
      if (!alive) return
      // The server is the record, and there are two different kinds of empty.
      //
      // sessionId null means the read FAILED or there is no session at all, and
      // replacing the screen with nothing on a flaky network is how somebody
      // loses a conversation they were halfway through. Keep the cache.
      //
      // sessionId present with no messages is a genuinely empty session, which
      // is what a fresh start looks like. Before this, that case fell through
      // the same guard and the stale localStorage copy stayed on screen, so a
      // reset on the server was invisible in the browser. Found by reading the
      // path rather than by shipping it.
      if (remote.length > 0) {
        setMessages(remote)
        saveTranscript(userId, remote)
      } else if (sessionId) {
        setMessages([])
        saveTranscript(userId, [])
      }
    })
    return () => {
      alive = false
    }
  }, [userId])

  // FOLLOW THE STREAM, BUT NEVER FIGHT SOMEBODY READING BACK.
  //
  // The old rule was "scroll to the bottom whenever anything changes", which
  // was fine when a reply arrived in one piece. A streaming reply changes
  // twenty times a second, and that same rule would drag the view back down
  // every time a fragment landed, so scrolling up to re-read what the coach
  // said thirty seconds ago would become impossible mid-reply.
  //
  // So: only auto-scroll when the view is already near the bottom, which is
  // the universal convention for this and the one people do not notice because
  // it does what they expected.
  const scroller = useRef(null)
  const stick = useRef(true)

  const onScroll = () => {
    const el = scroller.current
    if (!el) return
    // 64px of slack. Somebody who is a few pixels off the bottom is still
    // watching, and momentum scrolling rarely lands exactly at zero.
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 64
  }

  useEffect(() => {
    if (!stick.current) return
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, sending])

  // Separate effect, and `auto` rather than `smooth`, because a smooth scroll
  // retriggered on every fragment queues animations against each other and the
  // view visibly stutters. Following a stream should be invisible.
  useEffect(() => {
    if (!streaming || !stick.current) return
    bottomRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' })
  }, [streaming])

  // A new turn always snaps the view back, even if they had scrolled away.
  // Sending a message is an unambiguous statement that you want to see what
  // comes next.
  const stickToBottom = () => {
    stick.current = true
  }

  // Ask the coach with a thread that already ends in the user's message.
  // Split out so send and retry cannot drift apart.
  const ask = async (thread) => {
    setFailed(false)
    setSending(true)
    setStreaming('')
    stickToBottom()
    try {
      // THE DELTAS ARE FOR THE EYE. THE FINAL REPLY IS FOR THE RECORD.
      //
      // What gets written to the transcript is the `reply` the server returns,
      // never the text accumulated here, so a fragment lost on a bad
      // connection repairs itself the moment the reply lands and what is
      // stored always matches what the server actually persisted.
      const { reply } = await sendToCoach(
        thread,
        context,
        userId,
        methodMode ? 'method' : introMode ? 'intro' : null,
        (delta) => setStreaming((prev) => prev + delta)
      )
      const withReply = [...thread, { role: 'assistant', content: reply }]
      setMessages(withReply)
      saveTranscript(userId, withReply)
    } catch {
      // Leave the user's message in the thread. Losing what someone typed and
      // then telling them to try again is asking them to do the work twice.
      setFailed(true)
    } finally {
      setSending(false)
      setStreaming('')
    }
  }

  // Arriving from a step you just cleared. Fire the opening line once, then
  // drop the parameter so a refresh does not start a second drill on top of the
  // first, and so the back button does not either.
  useEffect(() => {
    if (!practiceStep || !userId) return
    supabase
      .from('activity_events')
      .select('id')
      .eq('user_id', userId)
      .eq('kind', 'practice_drill')
      .contains('metadata', { step: practiceStep })
      .limit(1)
      .then(({ data }) => setDrilledBefore((data || []).length > 0))
  }, [practiceStep, userId])

  const drillStarted = useRef(false)
  useEffect(() => {
    if (!introMode || drillStarted.current || sending || !userId) return
    drillStarted.current = true
    send('I am new here.')
    const next = new URLSearchParams(searchParams)
    next.delete('mode')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [introMode, userId, sending])

  // The callback practice opens in the rep's own words, so the transcript
  // reads like a person asking for help rather than like a mode switching on.
  useEffect(() => {
    if (!followupParam || !followup || drillStarted.current || sending || !userId) return
    drillStarted.current = true
    send(followupOpener(followup.unsold))
    const next = new URLSearchParams(searchParams)
    next.delete('mode')
    next.delete('unsold')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followupParam, followup, userId, sending])

  // The rehearsal opens in the rep's own words too.
  useEffect(() => {
    if (!agreementParam || !agreement || drillStarted.current || sending || !userId) return
    drillStarted.current = true
    send('My manager showed me something in our one on one. Help me rehearse it before they come back to look.')
    const next = new URLSearchParams(searchParams)
    next.delete('mode')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreementParam, agreement, userId, sending])

  useEffect(() => {
    if (!askParam || drillStarted.current || sending || !userId) return
    drillStarted.current = true
    send(askParam)
    const next = new URLSearchParams(searchParams)
    next.delete('ask')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askParam, userId, sending])

  useEffect(() => {
    if (!practiceStep || drillStarted.current || sending) return
    if (!userId) return
    drillStarted.current = true
    const step = STEPS.find((x) => x.number === practiceStep)
    send(`Run me a practice drill on Step ${practiceStep}${step ? `, ${step.title}` : ''}.`)
    const next = new URLSearchParams(searchParams)
    next.delete('practice')
    setSearchParams(next, { replace: true })
    // send is stable enough for this one-shot and adding it here would re-run
    // the effect on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceStep, userId, sending])

  const send = (text) => {
    const trimmed = (text || '').trim()
    if (!trimmed || sending) return
    const next = [...messages, { role: 'user', content: trimmed }]
    setMessages(next)
    saveTranscript(userId, next)
    setInput('')
    if (inputRef.current) inputRef.current.style.height = 'auto'
    ask(next)
  }

  const retry = () => {
    if (sending || messages.length === 0) return
    ask(messages)
  }

  // Two taps, because the transcript lives only in this browser and there is
  // no undo. Forgets it asked, so the button is not left armed.
  // Clears this screen, not the record. The server transcript stays, which is
  // the point of having one: a manager reviewing coaching, and the coach
  // remembering somebody, should not depend on whether they tapped this.
  const reset = () => {
    if (!confirmReset) {
      setConfirmReset(true)
      return
    }
    clearTranscript(userId)
    setMessages([])
    setFailed(false)
    setConfirmReset(false)
  }

  useEffect(() => {
    if (!confirmReset) return undefined
    const t = setTimeout(() => setConfirmReset(false), 4000)
    return () => clearTimeout(t)
  }, [confirmReset])

  const onKeyDown = (e) => {
    // Enter sends only where there is a real keyboard. A phone has no shift
    // key, so treating Enter as send there means a salesperson cannot write a
    // second line about the customer standing in front of them.
    if (e.key === 'Enter' && !e.shiftKey && hasKeyboard) {
      e.preventDefault()
      send(input)
    }
  }

  // Grow with the message, then scroll. Composing three sentences through a
  // one line slot is why people write one word answers to a coach.
  const autoGrow = (el) => {
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 140) + 'px'
  }

  return (
    <Flex
      direction="column"
      maxW="sheet"
      h={{
        base: 'calc(100dvh - 64px - 3rem - 6rem - env(safe-area-inset-bottom))',
        lg: 'calc(100vh - 64px - 5rem)',
      }}
      minH="420px"
    >
      {/* NO PAGE TITLE. You know which page you are on, and "Your corner guy /
          Coach" spent the top of a laptop screen telling you so. With it gone
          the conversation starts at the top of the window, which is the only
          thing on here anybody came for.

          The strip only exists once there is something to clear, so an empty
          coach has no chrome at all. */}
      <HStack justify="flex-end" pb={2} flexShrink={0} minH="40px">
        {messages.length > 0 && (
          confirmReset ? (
            <Button
              size="sm"
              minH="40px"
              px={4}
              variant="outline"
              borderRadius="pill"
              color="danger"
              borderColor="danger"
              onClick={reset}
            >
              Clear this screen?
            </Button>
          ) : (
            <IconButton
              aria-label="Clear this screen"
              icon={<RotateCcw size={16} />}
              variant="ghost"
              size="sm"
              minH="40px"
              minW="40px"
              onClick={reset}
            />
          )
        )}
      </HStack>

      <VStack
        ref={scroller}
        onScroll={onScroll}
        flex={1}
        overflowY="auto"
        align="stretch"
        spacing={4}
        pb={4}
        // An empty coach used to pin its opener to the top left of a very tall
        // box, which reads as content that failed to load rather than as an
        // invitation. Centred down the window when there is nothing to scroll,
        // and back to the top the moment there is.
        justify={messages.length === 0 ? 'center' : 'flex-start'}
        css={{ scrollbarWidth: 'thin' }}
        role="log"
        aria-live="polite"
        aria-label="Conversation with the coach"
      >
        {/* NO AVATAR IN THE EMPTY STATE. With the page title gone the sparkle
            was an anonymous circle introducing nothing, and the coach's own
            mark already sits beside every reply it makes. The words carry it.

            The opening line was 17px, which is body copy doing the job of the
            only sentence on a 1440px screen. It scales now, and stays modest on
            a phone where 17px was right all along. */}
        {messages.length === 0 && (
          <Box pt={6}>
            <Text
              fontSize={{ base: 'lead', md: '28px' }}
              fontWeight={600}
              letterSpacing={{ md: '-0.02em' }}
              lineHeight={1.15}
              mb={2}
            >
              {canCorrect
                ? (firstName ? `${firstName}, welcome aboard.` : 'Welcome aboard.')
                : (firstName ? `Ready when you are, ${firstName}.` : 'Ready when you are.')}
            </Text>
            <Text fontSize={{ base: 'body-sm', md: 'body' }} color="inkMuted" mb={6} lineHeight={1.6} maxW="measure">
              {canCorrect
                ? 'I run on your method, and here is where we are steering. Your actual words are going into all thirteen steps so a rep hears you and not a manual. That is the direction. Say the word if you would rather go somewhere else.'
                : progress.currentStep
                  ? `You're on Step ${progress.currentStep.number}, ${progress.currentStep.title}. I can quiz you, run role-play or talk through your next customer.`
                  : 'I can quiz you, run role-play on any of the 13 steps or talk through your next customer.'}
            </Text>
            {/* His own words, above the chips. One indexed read, rendered when
                it lands and skipped when it does not. This is the part of the
                old generated opener that was worth keeping: the only thing on
                this screen that proves his time went somewhere. */}
            <LastTaught enabled={canCorrect} />

            <Wrap spacing={2}>
              {(canCorrect ? AUTHOR_SUGGESTIONS : SUGGESTIONS).map((s) => (
                <WrapItem key={s}>
                  {/* These are the first thing a new user taps and they were
                      32px tall. They are also the cheapest way to teach someone
                      what the coach is for, so they should be easy to hit. */}
                  <Button size="sm" minH="40px" px={4} variant="outline" borderRadius="pill" fontWeight={500} onClick={() => send(s)}>
                    {s}
                  </Button>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
        )}

        {messages.map((m, i) => (
          <CoachTurn
            key={i}
            message={m}
            previous={messages[i - 1]}
            canCorrect={canCorrect}
            stepNumber={practiceStep || progress.currentStep?.number || null}
            isLast={i === messages.length - 1 && !sending}
            onChoose={send}
          />
        ))}

        {/* THINKING ONLY UNTIL THE FIRST WORD ARRIVES. That handover is most of
            what streaming actually buys: the wait stops being the length of the
            whole answer and becomes the length of the first token. */}
        {sending && !streaming && (
          <HStack spacing={3}>
            <Flex boxSize="30px" borderRadius="full" bg="ink" color="onInk" align="center" justify="center">
              <Sparkles size={14} />
            </Flex>
            <Spinner size="sm" color="inkDim" speed="0.8s" />
            <Text fontSize="body-sm" color="inkDim">Thinking</Text>
          </HStack>
        )}

        {streaming && <LiveTurn text={streaming} />}

        {failed && !sending && <FailedTurn onRetry={retry} busy={sending} />}
        <Box ref={bottomRef} />
      </VStack>

      {(dictation.state !== 'idle' || dictation.error) && (
        <HStack flexShrink={0} px={4} pb={2} spacing={3} fontSize="caption">
          {/* No "listening" caption. The button is pulsing red in front of them
              and a line of instructions while somebody is mid sentence is the
              opposite of smooth. Only the countdown survives, and only when the
              cap is close enough to matter. */}
          {dictation.state === 'recording' && dictation.remaining <= 20 && (
            <Text color="inkMuted" fontFamily="mono">{dictation.remaining}s left</Text>
          )}
          {dictation.state === 'starting' && (
            <>
              <Text color="inkMuted">Waiting for the microphone...</Text>
              <Text
                as="button"
                color="inkMuted"
                textDecoration="underline"
                onClick={dictation.cancel}
                _hover={{ color: 'ink' }}
              >
                Cancel
              </Text>
            </>
          )}
          {dictation.state === 'working' && <Text color="inkMuted">Writing that down...</Text>}
          {dictation.error && dictation.state === 'idle' && !dictation.guidance && (
            <>
              <Text color="danger">{dictation.error}</Text>
              <Text
                as="button"
                color="inkMuted"
                textDecoration="underline"
                onClick={dictation.clearError}
                _hover={{ color: 'ink' }}
              >
                Dismiss
              </Text>
            </>
          )}
        </HStack>
      )}

      {/* A blocked microphone cannot be fixed by trying again. The browser will
          not prompt a second time, so the only way through is a settings page,
          and the difference between finding it and giving up is whether we name
          the taps. Deliberately not a toast: it has to stay on screen while
          they go and do it. */}
      {dictation.guidance && dictation.state === 'idle' && (
        <Box
          flexShrink={0}
          mb={3}
          pt={4}
          pb={1}
          borderTop="1px solid"
          borderColor="line"
        >
          <HStack justify="space-between" align="flex-start" mb={2}>
            <Text fontWeight="600" color="ink" fontSize="body">
              {dictation.guidance.title}
            </Text>
            <Text
              as="button"
              fontSize="caption"
              color="inkMuted"
              textDecoration="underline"
              onClick={dictation.clearError}
              _hover={{ color: 'ink' }}
              flexShrink={0}
            >
              Close
            </Text>
          </HStack>
          <VStack as="ol" align="stretch" spacing={1.5} pl={0} sx={{ listStyle: 'none' }}>
            {dictation.guidance.steps.map((step, i) => (
              <HStack key={step} align="flex-start" spacing={2.5}>
                <Text
                  fontFamily="mono"
                  fontSize="caption"
                  color="accent.500"
                  minW="14px"
                  pt="1px"
                >
                  {i + 1}
                </Text>
                <Text fontSize="caption" color="ink2">{step}</Text>
              </HStack>
            ))}
          </VStack>
          {/* A chrome:// address cannot be a link. Browsers refuse to navigate
              to one from a page, deliberately, so the only thing we can do is
              hand over the text and make copying it one tap. */}
          {dictation.guidance.paste && (
            <Box mt={3}>
              <Text fontSize="caption" color="inkMuted" mb={1.5}>
                {dictation.guidance.paste.label}
              </Text>
              <HStack
                spacing={2}
                bg="bg"
                border="1px solid"
                borderColor="line"
                borderRadius="sm"
                px={3}
                py={2}
              >
                <Text
                  fontFamily="mono"
                  fontSize="11px"
                  color="ink2"
                  flex="1"
                  overflowX="auto"
                  whiteSpace="nowrap"
                >
                  {dictation.guidance.paste.value}
                </Text>
                <Text
                  as="button"
                  fontSize="caption"
                  color="accent.500"
                  fontWeight="600"
                  flexShrink={0}
                  onClick={() => {
                    navigator.clipboard?.writeText(dictation.guidance.paste.value)
                    setCopiedPath(true)
                    setTimeout(() => setCopiedPath(false), 2000)
                  }}
                >
                  {copiedPath ? 'Copied' : 'Copy'}
                </Text>
              </HStack>
            </Box>
          )}
          {dictation.guidance.note && (
            <Text fontSize="caption" color="inkMuted" mt={3}>
              {dictation.guidance.note}
            </Text>
          )}
          <Text fontSize="caption" color="inkMuted" mt={3}>
            {dictation.guidance.ours
              ? 'Nothing you can do about this one. Type for now and it will be fixed.'
              : 'You can always type instead. Nothing here needs a microphone.'}
          </Text>
        </Box>
      )}

      {/* THE COMPOSER HAS TO LOOK LIKE SOMETHING YOU CAN TAP.
          It was a bare textarea sitting under a hairline, so on a phone it read
          as a line of grey helper text rather than a control, and the only thing
          on the strip that looked pressable was the send button, which does
          nothing until you have already typed. A field that does not invite the
          first tap is the whole funnel for this product. */}
      <Box flexShrink={0} bg="bg" px={{ base: 2, md: 3 }} pt={2} pb={2}>
        <HStack
          align="flex-end"
          spacing={1}
          bg="surface"
          border="1px solid"
          // `line` measured 1.3 against the page here, which is a rule you infer
          // rather than see. A control asking for the first tap needs an edge.
          borderColor="lineStrong"
          borderRadius="24px"
          px={2}
          py={1}
          transition="border-color 120ms ease"
          _focusWithin={{ borderColor: 'ink' }}
        >
          <Textarea
          ref={inputRef}
          value={input}
          onChange={(e) => { setInput(e.target.value); autoGrow(e.target) }}
          onKeyDown={onKeyDown}
          // Short because it has to fit. On a 375px phone the textarea is 221px
            // wide, and the old wording wrapped to two lines inside a one line
            // box, so the second half was simply invisible. It also said tap
            // when the mic is now hold.
            placeholder={dictation.supported ? 'Ask anything, or hold the mic' : 'Ask the coach anything'}
          aria-label="Message the coach"
          variant="unstyled"
          // variant="unstyled" drops the theme's Textarea variant entirely, and
          // with it the _placeholder rule, so this fell back to Chakra's own
          // whiteAlpha.400. Measured on the rendered field that is rgb(82,82,83)
          // on rgb(27,27,29), a contrast of 2.2 against a 4.5 floor.
          // It is also the only line of copy that tells anybody the mic is there.
          _placeholder={{ color: 'inkDim' }}
          rows={1}
          resize="none"
          maxH="140px"
          overflowY="auto"
          fontSize="body"
          py={2}
        />
        {dictation.supported && (
          <IconButton
            // Hold to talk, the way Wispr Flow works with the Fn key, which is
            // the interaction Tyler asked for. A short press latches instead, so
            // a tap on a phone does not produce a quarter second of silence.
            //
            // Pointer events rather than onClick, and the pointer is captured so
            // sliding off the button still counts as letting go. Without capture
            // a drag off the edge leaves it recording with nothing to stop it.
            aria-label={dictation.state === 'recording' ? 'Stop recording' : 'Hold to talk'}
            icon={
              dictation.state === 'working' || dictation.state === 'starting' ? (
                <Spinner size="sm" />
              ) : (
                <Mic size={18} />
              )
            }
            borderRadius="full"
            // 44px, not the default 40. Apple's minimum is 44 and this is a
            // control somebody holds down with a thumb while talking, often in
            // a noisy showroom, which is the worst case for a small target.
            minW="44px"
            h="44px"
            variant={dictation.state === 'recording' ? 'solid' : 'ghost'}
            bg={dictation.state === 'recording' ? 'accent.500' : undefined}
            // Full strength, not muted. This is the primary way in for the one
            // person we built it for, and it was styled like the option you
            // reach for when the real one is broken.
            color={dictation.state === 'recording' ? 'onInk' : 'ink'}
            _hover={dictation.state === 'recording' ? { bg: 'accent.500' } : { bg: 'card', color: 'ink' }}
            isDisabled={sending || dictation.state === 'working'}
            onPointerDown={(e) => {
              e.preventDefault()
              e.currentTarget.setPointerCapture?.(e.pointerId)
              dictation.pressStart()
            }}
            onPointerUp={dictation.pressEnd}
            onPointerCancel={dictation.pressEnd}
            // Keyboard users get the same thing from the same button.
            onKeyDown={(e) => {
              if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
                e.preventDefault()
                dictation.pressStart()
              }
            }}
            onKeyUp={(e) => {
              if (e.key === ' ' || e.key === 'Enter') dictation.pressEnd()
            }}
            sx={
              dictation.state === 'recording' && !reduceMotion
                ? {
                    animation: 'j13mic 1.8s ease-in-out infinite',
                    '@keyframes j13mic': {
                      '0%': { boxShadow: '0 0 0 0 rgba(226, 112, 94, 0.55)' },
                      '70%': { boxShadow: '0 0 0 9px rgba(226, 112, 94, 0)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(226, 112, 94, 0)' },
                    },
                  }
                : undefined
            }
          />
        )}
          <IconButton
            aria-label="Send"
            icon={<ArrowUp size={18} />}
            borderRadius="full"
            minW="44px"
            h="44px"
            isDisabled={!input.trim() || sending}
            onClick={() => send(input)}
          />
        </HStack>
      </Box>
    </Flex>
  )
}
